/**
 * Splice undefined or null filter values
 *
 * @param params Filters object
 * @returns Part of passed filters spliced empty values
 */
export const spliceEmptyParams = <T extends object>(params: T): Partial<T> => {
  const result: Partial<T> = {}
  Object.keys(params).forEach((key) => {
    const paramKey: keyof T = <keyof T>key
    if (params[paramKey] !== undefined && params[paramKey] !== null) {
      result[paramKey] = params[paramKey]
    }
  })
  return result
}

export const formatDate = (date: string): string => {
  const dateInstance = new Date(date)
  return new Intl.DateTimeFormat('ru', {
    day: 'numeric',
    month: 'short',
  }).format(dateInstance)
}

export const guid = () => {
  return '10000000-1000-4000-8000-100000000000'.replace(
    /[018]/g,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (c: any) => {
      return (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16)
    }
  )
}

export const sinFromDegrees = (angle: number): number => {
  return Math.sin(angle * Math.PI / 180)
}

export const cosFromDegrees = (angle: number): number => {
  return Math.cos(angle * Math.PI / 180)
}

export const convertUrlToFile = async (url: string): Promise<File> => {
  const res = await fetch(url)
  const buf = await res.arrayBuffer()
  return new File([ buf ], guid(), { type: 'image/png' })
}

export const getBoundingRectFromSvgString = (svg: string): { width: number, height: number } => {
  return {
    width: parseInt(<string>svg.match(/.*?width="(\d+)/)?.[1]),
    height: parseInt(<string>svg.match(/.*?height="(\d+)/)?.[1]),
  }
}

export const getColorsFromSvgString = (svg: string): string[] => {
  return svg.match(/fill=".{0,12}"/g)?.map((item) => {
    return item.replace('fill="', '').replace('"', '').replace(' ', '')
  }) ?? []
}

export const convertPtToPx = (pt: number): number => {
  return pt * 4 / 3
}

export interface WaitingData<CallbackDataType> {
  callbackData: CallbackDataType // will be passed to callback
  waitingData: { [key: string]: boolean } // data waiting to load (callback will not be called if any key exists)
  allDataExecuted: boolean // is all data executed (callback will not be called if not all data has beed executed)
  callback: (data: CallbackDataType) => void // callback called after all data will be executed and loaded
}

/**
 * Call callback afted all waiting data executed and loaded
 * @param data Waiting data object
 */
export const runCallbackAfterWaitingDataLoaded = <CallbackDataType>(data: WaitingData<CallbackDataType>) => {
  if (data.allDataExecuted && Object.keys(data.waitingData).length === 0) {
    data.callback(data.callbackData)
  }
}

export const jsonParseWithUndefined = <T>(json: string): T => {
  const parsedData = JSON.parse(json)
  Object.keys(parsedData).forEach((prop) => {
    if (parsedData[prop] === null) {
      parsedData[prop] = undefined
    }
  })
  return parsedData as T
}

export const replaceNullToUndefined = <T extends object>(obj: T): T => {
  const clonedObj = JSON.parse(JSON.stringify(obj))
  Object.keys(obj).forEach((prop) => {
    if (clonedObj[prop] === null) {
      clonedObj[prop] = undefined
    }
  })
  return clonedObj as T
}

export const convertImageUrlToDataUrl = async (imageUrl: string): Promise<string> => {
  const imageBlob = await fetch(imageUrl)
    .then((r) => {
      return r.blob()
    })

  return await new Promise<string>((resolve) => {
    const reader = new FileReader()
    reader.onload = () => {
      return resolve(reader.result as string)
    }
    reader.readAsDataURL(imageBlob)
  })
}

